import { useTranslation } from 'react-i18next';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import type { AlertViewModel, AlertCriticality } from './alerts.gql.tsx';

interface AlertDetailModalProps {
	alert: AlertViewModel | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const criticalityConfig: Record<
	AlertCriticality,
	{
		icon: typeof Info;
		color: string;
		bgColor: string;
		badgeVariant: 'secondary' | 'outline' | 'destructive';
	}
> = {
	INFO: {
		icon: Info,
		color: 'text-blue-500',
		bgColor: 'bg-blue-50 dark:bg-blue-950',
		badgeVariant: 'secondary'
	},
	WARNING: {
		icon: AlertTriangle,
		color: 'text-yellow-500',
		bgColor: 'bg-yellow-50 dark:bg-yellow-950',
		badgeVariant: 'outline'
	},
	CRITICAL: {
		icon: AlertCircle,
		color: 'text-red-500',
		bgColor: 'bg-red-50 dark:bg-red-950',
		badgeVariant: 'destructive'
	}
};

export const AlertDetailModal: React.FC<AlertDetailModalProps> = ({
	alert,
	open,
	onOpenChange
}) => {
	const { t } = useTranslation(['alerts', 'common']);

	if (!alert) return null;

	const config = criticalityConfig[alert.criticality] || criticalityConfig.INFO;
	const Icon = config.icon;

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleString();
	};

	// Try to parse payload as JSON for better display
	const parsePayload = (payload: string | null | undefined) => {
		if (!payload) return null;
		try {
			return JSON.parse(payload);
		} catch {
			return payload;
		}
	};

	const parsedPayload = parsePayload(alert.payload);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className={`p-2 rounded-full ${config.bgColor}`}>
							<Icon className={`w-5 h-5 ${config.color}`} />
						</div>
						<div className="flex-1">
							<DialogTitle className="text-lg">
								{t(`alerts:codes.${alert.code}`, alert.code)}
							</DialogTitle>
							<DialogDescription className="mt-1">
								{formatDate(alert.createdAt)}
							</DialogDescription>
						</div>
						<Badge variant={config.badgeVariant}>
							{t(`alerts:criticality.${alert.criticality}`)}
						</Badge>
					</div>
				</DialogHeader>

				<div className="mt-4 space-y-4">
					{alert.payload && (
						<div>
							<h4 className="text-sm font-medium text-muted-foreground mb-2">
								{t('alerts:details.message')}
							</h4>
							{typeof parsedPayload === 'object' ? (
								<div className="bg-muted rounded-md p-3">
									<pre className="text-xs overflow-auto whitespace-pre-wrap">
										{JSON.stringify(parsedPayload, null, 2)}
									</pre>
								</div>
							) : (
								<p className="text-sm">{parsedPayload}</p>
							)}
						</div>
					)}

					<div className="grid grid-cols-2 gap-4">
						<div>
							<h4 className="text-sm font-medium text-muted-foreground mb-1">
								{t('alerts:details.code')}
							</h4>
							<p className="text-sm">{alert.code}</p>
						</div>
						<div>
							<h4 className="text-sm font-medium text-muted-foreground mb-1">
								{t('alerts:details.duration')}
							</h4>
							<p className="text-sm">
								{alert.duration != null
									? t(`alerts:duration.${alert.duration}`, { count: alert.duration })
									: t('alerts:duration.null')}
							</p>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
